import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge, Spinner, EmptyState } from "../../lib/ui";
import { fmtRp, fmtDateWIB } from "../../lib/constants";

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-app px-3 py-2 text-center">
      <p className="text-[11px] font-semibold text-ink-mute">{label}</p>
      <p className="font-heading text-sm font-extrabold text-ink">{value}</p>
    </div>
  );
}

export default function PriceHistoryTab() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { active_only: true } }).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.rows || [];
      setProducts(list.filter((p) => p.track_inventory !== false));
    }).catch(() => {});
  }, []);

  const load = useCallback(async (pid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/supplier-prices", { params: pid ? { product_id: pid } : {} });
      setData(data);
    } catch { setData({ history: [], stats: {} }); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(productId); }, [productId, load]);

  const trend = (i) => {
    const h = data?.history || [];
    if (i >= h.length - 1) return <Minus size={13} className="text-ink-mute" />;
    const diff = h[i].price - h[i + 1].price;
    if (diff > 0) return <span className="inline-flex items-center gap-0.5 text-danger-text"><ArrowUpRight size={13} /></span>;
    if (diff < 0) return <span className="inline-flex items-center gap-0.5 text-emerald-600"><ArrowDownRight size={13} /></span>;
    return <Minus size={13} className="text-ink-mute" />;
  };

  return (
    <div data-testid="sc-price-history">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <select className="cf-input !py-2 !text-sm sm:w-72" value={productId} onChange={(e) => setProductId(e.target.value)} data-testid="sc-price-product">
          <option value="">Semua produk</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
        </select>
        {data?.stats && (
          <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-5">
            <Stat label="Terakhir" value={data.stats.latest != null ? fmtRp(data.stats.latest) : "—"} />
            <Stat label="Rata-rata" value={data.stats.average != null ? fmtRp(data.stats.average) : "—"} />
            <Stat label="Tertinggi" value={data.stats.highest != null ? fmtRp(data.stats.highest) : "—"} />
            <Stat label="Terendah" value={data.stats.lowest != null ? fmtRp(data.stats.lowest) : "—"} />
            <Stat label="Catatan" value={data.stats.count || 0} />
          </div>
        )}
      </div>
      <Card>
        {loading ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : !data || (data.history || []).length === 0 ? <EmptyState image={null} title="Belum ada riwayat harga" subtitle="Riwayat harga tercatat otomatis saat penerimaan PO & pembelian." />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sc-price-table">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <tr><th className="py-2 pr-3">Tanggal</th><th className="py-2 pr-3">Produk</th><th className="py-2 pr-3">Supplier</th><th className="py-2 pr-3">Sumber</th><th className="py-2 pr-3 text-right">Harga</th><th className="py-2 pr-3 text-center">Tren</th></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.history.map((h, i) => (
                    <tr key={h.id || i} data-testid={`sc-price-row-${i}`}>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{fmtDateWIB(h.recorded_at)}</td>
                      <td className="py-2.5 pr-3 font-medium text-ink">{h.product_name}</td>
                      <td className="py-2.5 pr-3 text-ink-soft">{h.supplier_name || "—"}</td>
                      <td className="py-2.5 pr-3"><Badge tone="mute">{h.source || "—"}</Badge></td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-ink">{fmtRp(h.price)}</td>
                      <td className="py-2.5 pr-3 text-center">{trend(i)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
